<script setup lang="ts">
/**
 * Singbac Incorporation (Pre) — Draft Constitution (Private Company Limited by Shares).
 * · 앞부분(memorandum) 5개 조항 + subscribers 반복부 + 날짜는 원문 그대로 fill point 재현.
 * · subscribers([[if subscribers]] … [[/if subscribers]])는 행 추가/삭제 (화면 키 sub{id}_subscriber_*).
 *   normalize 가 sub{id}_subscriber_* → subscribers[] 배열로 만든다.
 * · 규정(regulations) 본문은 싱가포르 표준 모델 헌장(Companies Act 1967) — 핵심 조항을
 *   재현하고 나머지 정형 조항은 섹션 프레임워크로 표기(전체 정형문 축약; 실제 docx 는 전문 포함).
 */
import { onMounted, ref, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();

let seq = 1;
const subRows = ref<number[]>([1]);
function addSubscriber() {
  subRows.value.push(++seq);
}
function delSubscriber(id: number) {
  if (subRows.value.length > 1) subRows.value = subRows.value.filter((x) => x !== id);
}

/** 값이 채워진 sub{n} 행 id 목록 (편집 재진입 · 엔티티 자동시드 공용). */
function detectPresentRows(): number[] {
  const present: number[] = [];
  for (let i = 1; i <= 30; i++) {
    const has = ['subscriber_name', 'subscriber_address', 'subscriber_shares'].some((s) =>
      String(fill.values[`sub${i}_${s}`] ?? '').trim(),
    );
    if (has) present.push(i);
  }
  return present;
}
function syncPresentRows() {
  const present = detectPresentRows();
  if (!present.length) return;
  const merged = Array.from(new Set([...subRows.value, ...present])).sort((a, b) => a - b);
  if (merged.length !== subRows.value.length || merged.some((v, i) => v !== subRows.value[i])) {
    subRows.value = merged;
  }
  seq = Math.max(seq, ...merged);
}
onMounted(syncPresentRows);
watch(() => detectPresentRows().join(','), syncPresentRows);

/**
 * 회사(커스텀폼) 변경 시 subscriber 행을 명시적으로 재설정.
 *   자동감지(merge)는 축소를 못하므로, 재선택 후엔 이 메서드로 정확한 개수만 남긴다.
 */
function setSubscriberRows(ids: number[]) {
  const next = Array.isArray(ids) && ids.length ? [...ids] : [1];
  subRows.value = next;
  seq = Math.max(1, ...next);
}
defineExpose({ setSubscriberRows });

/** 표준 모델 헌장의 규정 섹션 구성 (Companies Act 1967) — 프레임워크 표기용. */
const REG_SECTIONS: string[] = [
  'Share capital and variation of rights',
  'Lien',
  'Calls on shares',
  'Transfer of shares',
  'Transmission of shares',
  'Forfeiture of shares',
  'Conversion of shares into stock',
  'Alteration of capital',
  'General meetings',
  'Proceedings at general meetings',
  'Votes of members',
  'Directors',
  'Powers and duties of directors',
  'Proceedings of directors',
  'Secretary',
  'The seal',
  'Accounts',
  'Audit',
  'Notices',
  'Winding up',
  'Indemnity',
  'Confidentiality',
];
</script>

<template>
  <div class="allot-doc">
    <!-- Cover / memorandum front matter -->
    <div class="doc-head" style="text-align:center">
      <div class="doc-meta">THE COMPANIES ACT 1967</div>
      <div class="doc-meta">REPUBLIC OF SINGAPORE</div>
      <div class="doc-meta" style="margin-top:6px">PRIVATE COMPANY LIMITED BY SHARES</div>
      <div class="doc-sec-title" style="margin-top:14px">CONSTITUTION OF</div>
      <div class="doc-lbl-title" style="font-size:15px">
        <CddVarField field-key="company_name" label="Company Name" />
      </div>
      <div class="doc-meta">(Incorporated in the Republic of Singapore)</div>
    </div>

    <p class="doc-p">
      1.&nbsp;&nbsp;The name of the company is <CddVarField field-key="company_name" label="Company Name" />
    </p>
    <p class="doc-p">
      2.&nbsp;&nbsp;The registered office of the company is situated in the Republic of Singapore.
    </p>
    <p class="doc-p">3.&nbsp;&nbsp;The liability of the members is limited.</p>
    <p class="doc-p">
      4.&nbsp;&nbsp;The share capital of the company is SGD
      <CddVarField field-key="share_capital_amount" label="Share Capital Amount" />
    </p>
    <p class="doc-p">
      5.&nbsp;&nbsp;We, the persons whose names and addresses are set out in this Constitution, desire to form a company
      in pursuance of this Constitution and we each agree to take the number of shares in the capital of the company set
      out against our respective names.
    </p>

    <!-- repeat: subscribers ([[if subscribers]] … [[/if subscribers]]) -->
    <div class="sub-block">
      <div v-for="(id, i) in subRows" :key="id" class="sub-row">
        <div class="sub-hd">
          <span class="sub-no">Subscriber {{ i + 1 }}</span>
          <button
            v-if="subRows.length > 1"
            type="button"
            class="row-del"
            title="Remove subscriber"
            @click="delSubscriber(id)"
          >✕</button>
        </div>
        <p class="doc-p">
          NAME: <CddVarField :field-key="`sub${id}_subscriber_name`" :label="`Subscriber ${i + 1} Name`" />
        </p>
        <p class="doc-p">
          ADDRESS: <CddVarField :field-key="`sub${id}_subscriber_address`" :label="`Subscriber ${i + 1} Address`" />
        </p>
        <p class="doc-p">
          NUMBER OF SHARES:
          <CddVarField :field-key="`sub${id}_subscriber_shares`" :label="`Subscriber ${i + 1} Shares`" />
        </p>
      </div>
      <button type="button" class="row-add" @click="addSubscriber">+ Add subscriber</button>
    </div>

    <p class="doc-p" style="margin-top:12px">
      Dated this: <CddVarField field-key="constitution_date" label="Constitution Date" />
    </p>

    <!-- Regulations -->
    <div class="doc-sec-title" style="margin-top:18px">Interpretation</div>
    <p class="doc-p">6.&nbsp;&nbsp;—(1) In this Constitution —</p>
    <p class="doc-p">&ldquo;Act&rdquo; means the Companies Act 1967;</p>
    <p class="doc-p">&ldquo;annual general meeting&rdquo; has the meaning set out in section 175 of the Act;</p>
    <p class="doc-p">
      &ldquo;applicable law&rdquo; means any applicable treaty, statute, rule, regulation, order, directive, code,
      judgment, decree, injunction or any interpretation, determination, award, permit, license, authorization,
      directive requirement, ruling or decision of, agreement with, or by a government authority;
    </p>
    <p class="doc-p">
      &ldquo;board of directors&rdquo; means the board of directors of the company; &ldquo;directors&rdquo; means the
      directors of the company;
    </p>
    <p class="doc-p">
      &ldquo;electronic record&rdquo; means a record generated, communicated, received or stored by electronic means in
      an information system or for transmission from one information system to another;
    </p>
    <p class="doc-p">
      &ldquo;electronic register of members&rdquo; means the electronic register of members kept and maintained by the
      Registrar for private companies under section 196A of the Act;
    </p>
    <p class="doc-p">
      &ldquo;extraordinary general meeting&rdquo; means any general meeting other than an annual general meeting;
    </p>
    <p class="doc-p">&ldquo;general meeting&rdquo; means a general meeting of the company;</p>
    <p class="doc-p">&ldquo;member&rdquo; means a member of the company;</p>
    <p class="doc-p">&ldquo;Registrar&rdquo; has the same meaning as in section 4(1) of the Act;</p>
    <p class="doc-p">&ldquo;seal&rdquo; means the common seal of the company;</p>
    <p class="doc-p">
      &ldquo;secretary&rdquo; means a secretary of the company appointed under section 171 of the Act.
    </p>

    <div class="doc-sec-title" style="margin-top:14px">Private Company</div>
    <p class="doc-p">7.&nbsp;&nbsp;The company is a private company and accordingly —</p>
    <p class="doc-p indent">
      a.&nbsp;&nbsp;the number of the members of the company (not including employees or former employees of the company
      or any of its subsidiaries) shall be limited to not more than fifty (50);
    </p>
    <p class="doc-p indent">
      b.&nbsp;&nbsp;the right to transfer the shares of the company shall be restricted according to the provisions set
      out in this constitution.
    </p>

    <p class="doc-p" style="margin-top:12px">
      8. and following — The company adopts the regulations set out in the standard Constitution of a private company
      limited by shares under the Companies Act 1967, comprising the following sections:
    </p>
    <ul class="reg-list">
      <li v-for="s in REG_SECTIONS" :key="s">{{ s }}</li>
    </ul>

    <div class="doc-sec-title" style="margin-top:14px">Indemnity</div>
    <p class="doc-p">
      118.&nbsp;&nbsp;Every officer of the company is to be indemnified out of the assets of the company against any
      liability (other than any liability referred to in section 172B(1)(a) or (b) of the Act) incurred by the officer to
      a person other than the company attaching to the officer in connection with any negligence, default, breach of duty
      or breach of trust.
    </p>

    <div class="doc-sec-title" style="margin-top:14px">Confidentiality</div>
    <p class="doc-p">
      120.&nbsp;&nbsp;No member shall disclose any confidential information regarding any detail of the company's trade or
      any matter which may be in the nature of a trade secret, secret process or any other confidential information
      related to the conduct of the business of the company or which, in the opinion of the directors, it would be
      inexpedient in the interests of the members of the company to communicate such information to the public save as may
      be required by the Act or any applicable law.
    </p>
  </div>
</template>

<style scoped>
.indent { padding-left: 20px; }
.sub-block { margin: 10px 0; padding: 10px 12px; border: 1px solid #e2e6ee; border-radius: 8px; background: #fafbfe; }
.sub-row + .sub-row { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #d9deea; }
.sub-hd { display: flex; align-items: center; gap: 8px; }
.sub-no { font-size: 11px; font-weight: 800; color: #6b7a94; }
.row-del { margin-left: auto; border: none; background: none; color: #c0392b; cursor: pointer; font-size: 12px; }
.row-add { margin-top: 8px; border: 1px dashed #9aa4b2; background: #f7f8fa; border-radius: 6px; padding: 3px 10px; font-size: 11px; color: #3182f6; cursor: pointer; }
.reg-list { margin: 6px 0 0 22px; padding: 0; }
.reg-list li { font-size: 12px; color: #333; line-height: 1.7; }
</style>
